// Please do not use process.env for environment variables.
//
// There should be very few cases where you use `process.env` (for example,
// NODE_ENV).
//
// In order to keep the application immutable, we should declare the
// environment variables in public/index.html on the `window` object, then set
// them to constants here.
//
// In order to use environment variables, make sure that the environment
// variable is named 'REACT_APP_<ENV_VAR>' so that `create-react-app` can
// interpolate the env var into `public/index.html`.
//
// For more information about immutable webapps, check out:
//
// https://immutablewebapps.org/#-workflow

window.env = window.env ?? {};

const VITE_ENV =
  window.env.VITE_MODE === 'test'
    ? 'test'
    : window.env.VITE_PROD === 'true'
    ? 'production'
    : 'development';

export const ENV = VITE_ENV;

// The single list of known hosts: Sentry allowUrls, GA/env selection and every
// admin-host behavior derive from these. Match hostnames exactly, never by
// substring, so look-alike hosts and redirect params can't match.
export const ADMIN_HOSTS = {
  production: 'crew.postscript.io',
  development: 'dev-crew.postscript.io',
};

export const MERCHANT_HOSTS = {
  production: 'app.postscript.io',
  development: 'dev.postscript.io',
};

export const HOST_ENVS = {
  [MERCHANT_HOSTS.production]: 'prod',
  [ADMIN_HOSTS.production]: 'prod',
  [MERCHANT_HOSTS.development]: 'dev',
  [ADMIN_HOSTS.development]: 'dev',
  localhost: 'local',
};

export const getHostEnv = (href) => {
  try {
    return HOST_ENVS[new URL(href).hostname];
  } catch {
    return undefined;
  }
};

// The admin hosts serve the SPA behind Cloudflare Access; the edge proxies
// same-origin /api/* to internal-api, so the build-time host is bypassed there.
export const IS_ADMIN_HOST = Object.values(ADMIN_HOSTS).includes(
  window.location.hostname,
);

// The admin and merchant host pair for this build. Chosen by build mode, not by
// ENV: every `vite build` sets VITE_PROD, including the dev deploy
// (`vite build --mode development`), so ENV is 'production' on dev.postscript.io.
const HOST_PAIR =
  window.env.VITE_MODE === 'production' ? 'production' : 'development';
export const ADMIN_HOST = ADMIN_HOSTS[HOST_PAIR];
export const MERCHANT_HOST = MERCHANT_HOSTS[HOST_PAIR];
export const ADMIN_ORIGIN = `https://${ADMIN_HOST}`;
export const MERCHANT_ORIGIN = `https://${MERCHANT_HOST}`;
export const ACORN_ORIGIN =
  HOST_PAIR === 'production'
    ? 'https://app.pscrpt.com'
    : 'https://acorn-dev.pscrpt.com';

export const API_HOST = window.env.VITE_API_HOST;
// Absolute build-time host, for URLs that are saved and fetched outside the
// browser (e.g. vcard media_url fetched by carriers). Never '/api'.
export const PUBLIC_INTERNAL_API_HOST = window.env.VITE_INTERNAL_API_HOST;
export const INTERNAL_API_HOST = IS_ADMIN_HOST
  ? '/api'
  : window.env.VITE_INTERNAL_API_HOST;
export const GLOBAL_OFFERS_API_HOST = window.env.VITE_GLOBAL_OFFERS_API_HOST;
export const CASHBACK_API_HOST = window.env.VITE_CASHBACK_API_HOST;
export const SDK_HOST = window.env.VITE_SDK_HOST;
export const SDK_API_HOST = window.env.VITE_SDK_API_HOST;
export const SENTRY_RELEASE = window.env.VITE_SENTRY_RELEASE;
export const SENTRY_SAMPLE_RATE = window.env.VITE_SENTRY_SAMPLE_RATE;
export const STRIPE_PUBLIC_KEY = window.env.VITE_STRIPE_PUBLIC_KEY;
export const WEBSOCKET_HOST = window.env.VITE_WEBSOCKET_HOST;
export const AMPLITUDE_CONSOLE_LOG = window.env.VITE_AMPLITUDE_CONSOLE_LOG;
export const CSRF_TOKEN_NAME = window.env.VITE_CSRF_TOKEN_NAME;
export const INTERCOM_APP_ID = window.env.VITE_INTERCOM_APP_ID;
export const APP_ENV = window.env.VITE_MODE;
export const SDK_IMAGES_HOST = window.env.VITE_SDK_IMAGES_HOST;
